import React, {useMemo, useState, useCallback} from 'react';
import Joyride, {CallBackProps, Step, Styles, EVENTS, type FloaterProps, STATUS} from 'react-joyride';
import {createSubscribedCustomTooltipWrapper} from './SubscribedCustomTooltip';
import AppTourSkipModal from '../shared/molecule/onboarding/AppTourSkipModal';
import {TourState} from '@meprism/shared/src/redux/profile/ProfileTypes';
import OnboardingDialog from '../shared/molecule/onboarding/OnboardingDialog';
import EndTourModal from '../shared/molecule/onboarding/EndTourModal';
import {useNavigate} from "react-router-dom";
import { MPTourEventType } from '../../services/AnalyticsEvents';

interface SubscribedAppTourProps extends Partial<FloaterProps> {
  steps: Step[]
  handleUpdateTourState: (tourState: TourState, mpTourEvent: MPTourEventType) => void
}

const tourStyles: Partial<Styles> = {
  options: {
    primaryColor: '#F3F3F3',
    backgroundColor: 'rgba(0,0,0,0.85)',
    textColor: '#fff',
    zIndex: 2100,
  },
  buttonBack: {
    display: 'none',
  },
  buttonSkip: {
    display: 'none',
  },
};


const SubscribedAppTourComponent: React.FC<SubscribedAppTourProps> = ({steps, handleUpdateTourState}) => {

  const [showTour, setShowTour] = useState(true);
  const navigate = useNavigate();
  const [showSkipModal, setShowSkipModal] = useState(false);
  const [showTourEndModal, setShowTourEndModal] = useState(false)
  const [showOnboardingDialog, setShowOnboardingDialog] = useState(false)
  const floaterProps = useMemo(() => ({
    hideArrow: true,
  }), []);


  const locale = useMemo(() => ({
    last: 'Finish',
    next: 'Next',
    skip: 'Skip',
  }), []);

  const handleTourSkip = useCallback(() => {
    setShowTour(false);
    setShowSkipModal(true);
  }, []);
  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const {status, type, step} = data;
    
    if (type === EVENTS.STEP_BEFORE) {
      [100, 300, 600, 1000].forEach(delay =>
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
        }, delay)
      );

      if (step.target === '.address-sidebar') {
        const addressTab = document.querySelector('.address-sidebar');
        if (addressTab) {
          (addressTab as HTMLElement).click();
        }
      }
      if (step.target === '.phone-email-sidebar') {
        const phoneEmailTab = document.querySelector('.phone-email-sidebar');
        if (phoneEmailTab) {
          (phoneEmailTab as HTMLElement).click();
        }
      }
      if (step.target === '.opt-out-requests-tab') {
        const optOutRequestsTab = document.querySelector('.opt-out-requests-tab');
        if (optOutRequestsTab) {
          (optOutRequestsTab as HTMLElement).click();
        }
      }
    }

    if (type === EVENTS.STEP_AFTER) {
      if (step.target === '.opt-outs-table') {
        setShowTour(false);
        setShowOnboardingDialog(true);
      }
      if (step.target === '.basic-info-linkedin-url') {
        setShowTour(false);
        const linkedinUrlTextField = document.querySelector('.basic-info-linkedin-url-textfield');
        if (linkedinUrlTextField) {
          const inputEl = linkedinUrlTextField as HTMLElement;
          inputEl.focus();
        }
        setTimeout(() => {
          setShowTour(true)
        }, 2000)
      } else if (step.target === '.address-sidebar-button') {
        const addAddressButton = document.querySelector('.address-sidebar-button');
        if (addAddressButton) {
          (addAddressButton as HTMLElement).click();
        }
        setShowTour(false);
        setTimeout(() => {
          setShowTour(true);
        }, 2000);
      } else if (step.target === '.phone-email-button') {
        const addPhoneEmailButton = document.querySelector('.phone-email-button');
        if (addPhoneEmailButton) {
          (addPhoneEmailButton as HTMLElement).click();
        }
        setShowTour(false);
        setTimeout(() => {
          setShowTour(true);
        }, 2000);
      }
    }


    if ((status === STATUS.FINISHED) && type === EVENTS.TOUR_END) {
      setTimeout(() => {
        setShowTour(false);
        setShowTourEndModal(true);
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Joyride
        steps={steps}
        run={showTour}
        continuous
        showSkipButton={false}
        tooltipComponent={createSubscribedCustomTooltipWrapper(handleTourSkip)}
        showProgress={false}
        styles={tourStyles}
        callback={handleJoyrideCallback}
        disableCloseOnEsc
        locale={locale}
        floaterProps={floaterProps}
      />
      <AppTourSkipModal
        open={showSkipModal}
        handleGoBack={() => {
          setShowSkipModal(false);
          setShowTour(true);
        }}
        handleSkip={() => {
          setShowSkipModal(false);
          handleUpdateTourState({subscribed_user_tour_state: 'skipped'}, {event:"App Tour Skipped - Subscribed", source:"Web App"});
        }}
      />
      <EndTourModal
        open={showTourEndModal}
        onClose={() => {
          setShowTourEndModal(false);
          handleUpdateTourState({subscribed_user_tour_state: 'completed'}, {event:"App Tour Finished - Subscribed", source:"Web App"});
          navigate('/dashboard');
        }}
        onUpgrade={() => {
          setShowTourEndModal(false);
          handleUpdateTourState({subscribed_user_tour_state: 'completed'}, {event:"App Tour Finished - Subscribed", source:"Web App"});
          navigate('/dashboard');
        }}
        buttonText='Finish Tour'
        title='Thank you for subscribing to Priwall'
        description='We look forward to protecting your privacy.'
      />
      <OnboardingDialog
        open={showOnboardingDialog}
        title="This is the Extended Coverage Tab"
        description="These are non-scannable data brokers. You may receive email communications acknowledging the data removal request. This is normal and indicates Priwall is working on your behalf behind the scenes."
        actionLabel="Continue"
        onAction={() => {
          navigate('/dashboard/profile');
          setShowOnboardingDialog(false);
          setShowTour(true);
        }}
      />
    </>
  );
};

SubscribedAppTourComponent.displayName = 'SubscribedAppTour';

export const SubscribedAppTour = React.memo(SubscribedAppTourComponent);